const FILTERS = [
  "INPUT",
  "SELECT",
  "TEXTAREA",
];

export function Query({ selector = "*", index = 0, ...context } = { selector: "*"}) {
  return {
    isMounted: false,
    filters: {},
    index,
    ...context,
    // Keeps track the list of items in current page.
    current: [],
    get first() {
      return Number(this.index) === 0;
    },
    get last() {
      return Number(this.index) === this.items.length - 1;
    },
    get nextIndex() {
      const count = this.items.length;
      return (this.index + 1 + count) % count;
    },
    /**
     * @returns {HTMLElement[]}
     */
    get items() {
      const children = this.$refs.itemList?.children || [];
      return Array.from(children).filter((item) => item.matches(selector));
    },
    pages: [],
    /**
     * @returns {HTMLElement[]}
     */
    get results() {
      const {itemList} = this.$refs;
      if (!itemList) return [];

      const form = this.form;

      let {
        limit,
        offset = 0,
        ...filters
      } = this.filters;

      offset = Number(offset);
      limit = Number(limit);

      const selectors = Object.entries(filters)
      .map(([k,v]) => {
        let selector = `data-${k.toLowerCase()}`;
        const input = form.elements[k];
        // Only filter against value if there is value, or filter is required.
        if (v || input.required) {
          if (input.type === "text" || input.type === "search") {
             selector += `*="${v}" i`;
          } else {
            selector += `="${v}" i`;
          }
        }

        return `[${selector}]`;
      });

      if (!selectors.length) {
        return [];
      }

      /**
       * @type {HTMLElement[]}
       */
      const results = Array.from(itemList.querySelectorAll(selectors.join("")));
      if (!limit) {
        return results;
      }
      const pages = this.pages = [];
      for (let i = 0; i < results.length; i += limit) {
        pages.push(results.slice(i, i + limit));
      }

      return pages[offset] || [];
    },
    get form() {
      /**
       * @type {HTMLFormElement}
       */
      let form = this.$refs.form;
      if (form?.hasAttribute("form")) {
        form = document.getElementById(form.getAttribute("form"));
      }
      return form;
    },
    mounted() {
      this.updateSelects();
      this.isMounted = true;

      const form = this.form;
      // Update form bindings with value from search params.
      if (form) {
        const url = new URL(location);
        const elements = Array
          .from(form.elements)
          .filter(({nodeName}) => FILTERS.includes(nodeName));

        for (const element of elements) {
          const key = element.name;
          const value = url.searchParams.get(key);
          this.filters[key] = value || "";

          // If there is a form element depending on this, it's waiting
          // for change from this. We trigger it manually.
          const dependent = elements.find((el) => el.dataset.dependencies?.includes(key));
          if (dependent) {
            element.value = value || "";
            element.dispatchEvent(new CustomEvent("change"));
          }
        }
      }
    },
    unmounted() {

    },
    /**
     * Updates select's options based on items.
     */
    updateSelects() {
      /**
       * @type {HTMLFormElement}
       */
      let form = this.form;
      if (!form) return;
      const elements = form.elements;

      for (const control of elements) {
        if (control.tagName !== "SELECT") continue;

        /**
         * @type {HTMLSelectElement}
         */
        const {
          name,
          dataset,
        } = control;
        /**
         * @type {string[]}
         */
        const deps = dataset.dependencies?.trim().split(/,;>\s/) || [];
        const filters = {};
        for (const name of deps) {
          const dep = elements[name];
          filters[name] = dep.value;

          dep.addEventListener("change", () => {
            this.updateOptions(control, {
              [name]: dep.value,
            });
          });
        }

        this.updateOptions(control, filters);
      }
    },
    /**
     * Updates list of options of a select control.
     * @param {HTMLSelectElement} select
     * @param {Object} deps
     */
    updateOptions(select, deps = {}) {
      const name = select.name;
      /**
       * @type {string[]}
       */
      const options = [];

      itemsLoop: for (const item of this.items) {
        const value = item.dataset[name.toLowerCase()];
        if (!value) continue;
        if (options.includes(value)) continue;

        for (const [key, value] of Object.entries(deps)) {
          if (item.dataset[key.toLowerCase()] !== value) {
            continue itemsLoop;
          }
        }

        options.push(value);
      };

      // `select.options` is a live list, so we have to use basic for
      // loop to remove options.
      for (let i = select.options.length - 1; i >= 0; i--) {
        const option = select.options[i];
        if (!option.value) continue;
        option.remove();
      }

      options.sort();

      for (const option of options) {
        select.insertAdjacentHTML(
          "beforeend",
          `<option value="${option}">${option}</option>`,
        );
      }

      select.toggleAttribute("disabled", !options.length);
    },
    submit(event) {
      const form = this.form;
      const method = event?.submitter.formMethod || form.method;
      
      if (method.toLowerCase() === "post") {
        return form.submit();
      }

      if (form) {
        event?.preventDefault();
        const entries = new FormData(form);
        this.filters = Object.fromEntries(entries);

        // Updates URL with form data.
        const url = new URL(location);
        for (const [key, value] of entries) {
          url.searchParams.set(key, value);
        }
        history.pushState({}, "", url);
      }
    },
    reset() {
      setTimeout(() => {
        this.submit();
      });
    },
    /**
     * Slides to a specific slide by its index.
     * @param {number} index Index of the slide to go to
     * @param {number} direction -1 for previous, 1 for next, 0 for no change.
     * @returns {number} The new active slide index
     */
    goto(index = this.index, direction = (index - this.index)) {
      const items = this.items;
      const count = items.length;

      index = this.index = Math.clamp(index, 0, count - 1);
      let inline = "start";
      const item = items[index];

      if (item) {
        // `scrollIntoView` with `container` option is not supported in Safari.
        // item.scrollIntoView({
        //   block: "nearest",
        //   inline: "nearest",
        //   container: "nearest",
        // });

        this.$refs.itemList.scrollTo({
          left: item.offsetLeft,
        });
      }

      return index;
    },
  };
};
